import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/app/Avatar";
import { LogOut, UserRound, Building2 } from "lucide-react";

const ROLE_LABEL = { owner: "Owner", sales_director: "Sales Director", sales: "Sales", finance: "Finance" };

/**
 * AccountMenu — top-right user popover, glassy light theme.
 */
export default function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const go = (path) => { setOpen(false); navigate(path); };
  const handleLogout = async () => { setOpen(false); await logout(); navigate("/login"); };

  const isOwner = user?.role === "owner";

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="account-menu-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        className="group inline-flex items-center gap-2.5 h-11 pl-1 pr-3 rounded-full bg-white hover:bg-[#F4F5F7] transition-all shadow-[0_2px_8px_rgba(20,22,31,0.06)] hover:shadow-[0_4px_14px_rgba(20,22,31,0.10)]"
      >
        <Avatar name={user?.name} picture={user?.picture} size="sm" className="!w-9 !h-9 !text-sm !border-0" />
        <div className="hidden sm:flex flex-col items-start leading-tight min-w-0 max-w-[140px]">
          <span className="text-xs font-bold text-[#090808] truncate" data-testid="account-trigger-name">
            {user?.nickname || user?.name || "User"}
          </span>
          <span className="text-[10px] text-[#626061] truncate" data-testid="account-trigger-role">
            {ROLE_LABEL[user?.role] || user?.role || "User"}
          </span>
        </div>
      </button>

      {open && (
        <div
          role="menu"
          data-testid="account-menu-popover"
          className="glass-menu absolute right-0 top-[52px] w-80 z-50 rounded-3xl overflow-hidden text-[#14161F]"
        >
          {/* Identity block */}
          <div className="px-5 pt-5 pb-4 flex items-start gap-3 border-b border-black/5">
            <Avatar name={user?.name} picture={user?.picture} size="lg" className="ring-2 ring-white" />
            <div className="min-w-0 flex-1">
              <div className="text-base font-bold truncate font-display" data-testid="account-menu-name">{user?.name}</div>
              <div className="text-[11px] text-[#626061] truncate mt-0.5" data-testid="account-menu-email">{user?.email}</div>
              <div className="mt-2">
                <span className="inline-flex items-center h-5 px-2 rounded-full bg-[#14161F] text-white text-[10px] font-bold uppercase tracking-widest">
                  {ROLE_LABEL[user?.role] || user?.role || "user"}
                </span>
              </div>
            </div>
          </div>

          {/* Menu items */}
          <div className="py-2">
            <MenuBtn
              testid="account-menu-profile"
              icon={UserRound}
              label="Profile Saya"
              hint="Ubah nama, foto, password"
              onClick={() => go("/settings?tab=profile")}
            />
            {isOwner && (
              <MenuBtn
                testid="account-menu-company"
                icon={Building2}
                label="Pengaturan Perusahaan"
                hint="Logo & nama untuk PDF report"
                onClick={() => go("/settings?tab=company")}
              />
            )}
            <div className="my-1.5 mx-4 border-t border-black/5" />
            <MenuBtn
              testid="account-menu-logout"
              icon={LogOut}
              label="Keluar"
              hint="Log out dari akun"
              danger
              onClick={handleLogout}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuBtn({ icon: Icon, label, hint, onClick, testid, danger }) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
        danger ? "hover:bg-[#FFD7B5]/70 text-[#FF6B7D]" : "hover:bg-black/5 text-[#14161F]"
      }`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
        danger ? "bg-[#FFD7B5] text-[#FF6B7D]" : "bg-black/5 text-[#14161F]"
      }`}>
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate">{label}</div>
        <div className={`text-[11px] truncate ${danger ? "text-[#FF6B7D]/80" : "text-[#626061]"}`}>{hint}</div>
      </div>
    </button>
  );
}
